/**
 * Created by rathompson on 11/3/21
 * File: PermissionsTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ConnectedTable,
  ConnectedTableLayout,
  TableProvider,
  useTableInstance
} from '@panwds/react-table'
import {
  LABEL_HEADER_ROLE_DESC,
  LABEL_HEADER_PERMISSION, PLACEHOLDER_SEARCH_PERMISSION, LABEL_NO_PERMISSIONS,
} from '../../../constants/LabelConstants'
import styled from 'styled-components'
import ConnectedTableFilter from '../../../../common/components/table/tableFilter/ConnectedTableFilter'
import PermissionCell from './PermissionCell'
import { ARRAY, OBJECT } from '../../../../common/utils/Utils'
import { Body, Checkbox } from '@panwds/react-ui'
import V5TableWrapper from '../../../../common/components/table/V5TableWrapper'

const CONTAINER = styled(V5TableWrapper)`
  display: flex;
  flex-direction: column;
  button[id^=table] {
    color: #333333;
  }

  button[aria-label="Previous"] {
    color: #333333;
  }
  button[aria-label="Next"] {
    color: #333333;
  }

  label {
    margin: 0;
  }
  height: 100%;
`

const TABLE_CONTAINER = styled.div`
  position: absolute;
  inset: 0 0 0 0;
  height: 100%;
  display: flex;
  flex-direction: column;

  .tw-border-solid.tw-border-gray-200.tw-border-0.tw-flex.tw-relative.tw-bg-white.tw-border.tw-overflow-auto {
    flex: 1 1;
  }
  button[id^=table] {
    color: #333333;
  }

  button[aria-label="Previous"] {
    color: #333333;
  }
  button[aria-label="Next"] {
    color: #333333;
  }

  div[role="tooltip"] {
    z-index: 1055;
  }

  .tw-flex.tw-bg-white {
    flex: 1 1;
  }

  input[type=checkbox] {
    min-height: 1rem;
    min-width: 1rem;
    font-family: inherit !important;
    position: initial !important;
    -moz-appearance: auto !important;
    -webkit-appearance: checkbox !important;
    -o-appearance: auto !important;

    &[disabled] {
      background-image: none;
      border-width: 1px;
      border-style: solid;
      border-radius: 0.063rem;
      --tw-border-opacity: 1;
      border-color: rgba(218,219,219,var(--tw-border-opacity));
      background-color: transparent;
    }
  }
  input[type=checkbox]::before {
    display: none !important;
  }
`

const INPUT_CONTAINER = styled.div`
  span {
    width: 100%;
  }
`

/**
 *   Permissions table list
 */
const PermissionsTable = ({
  data = [],
  tableRef,
  tableInstanceRef,
  enableRowSelect = true,
  enablePagination = true,
  enableSearch = true,
  tableState = {},
  useLocks = false,
  expanded: _expanded = true
}) => {

  const [searchInput, setSearchInput] = useState('')
  const onSelect = useCallback((row) => {
    const updateChildren = (subRows, isSelected) => {
      subRows.forEach(row => {
        tableRef.current?.toggleRowSelected(row.id, isSelected)
        if (ARRAY.hasArray(row.subRows)) {
          updateChildren(row.subRows, isSelected)
        }
      })
    }
    const isSelected = !row.isSelected
    tableRef.current?.toggleRowSelected(row.id, isSelected)
    if (ARRAY.hasArray(row.subRows)) {
      updateChildren(row.subRows, isSelected)
    }
  }, [tableRef])

  const columns = useMemo(() => [
    {
      accessor: 'name',
      Header: () => {
        const table = useTableInstance()
        const { onAllRowSelect, flatRows, toggleRowSelected } = table

        const hasSelected = ARRAY.hasArray(table?.selectedFlatRows)
        const isAllRowsSelected = table?.isAllRowsSelected
        const indeterminate = hasSelected && !isAllRowsSelected

        const onCheck = useCallback((event) => {
          onAllRowSelect ? onAllRowSelect(event.target.checked) : flatRows.forEach((row) => {
            if (row.original.disabled && !row.isSelected) {
              toggleRowSelected(row.id, false)
            }
            else if (!indeterminate && !isAllRowsSelected) {
              toggleRowSelected(row.id, true)
            }
            else if (indeterminate) {
              toggleRowSelected(row.id, false)
            }
            else {
              toggleRowSelected(row.id, false)
            }
          })
        }, [indeterminate, isAllRowsSelected, onAllRowSelect, flatRows, toggleRowSelected])

        if (enableRowSelect) {
          return (
            <div className='cm-flex cm-absolute cm-inset-0 cm-items-center'>
              <div className='cm-box-border cm-bg-white dark:cm-bg-gray-1000 cm-h-full cm-px-4 cm-font-bold cm-py-2'>
                <Checkbox
                  checked={isAllRowsSelected}
                  indeterminate={indeterminate}
                  onChange={onCheck}
                />
              </div>
              <div className='cm-box-border cm-bg-white dark:cm-bg-gray-1000 cm-h-full cm-px-4 cm-flex-grow cm-border-solid cm-border-gray-200 cm-border-0 cm-border-l dark:cm-border-gray-700 cm-font-bold cm-py-2'>
                {LABEL_HEADER_PERMISSION}
              </div>
            </div>
          )
        }
        return LABEL_HEADER_PERMISSION
      },
      Cell: (props) => <PermissionCell {...{ ...props, enableRowSelect, onSelect, tableRef }} />,
      customExpansionToggle: true,
      customGroupCount: true,
      width: 500,
      id: 'name'
    },
    {
      accessor: 'description',
      Header: LABEL_HEADER_ROLE_DESC,
      disableGlobalFilter: true,
      Cell: ({ row, value }) => {
        return (
          <div className='cm-whitespace-normal cm-w-full'>
            <Body size="lg" truncate>{value}</Body>
          </div>
        )
      },
      id: 'description',
    }
  ], [enableRowSelect, onSelect, tableRef])

  const resolvedData = useMemo(() => {
    if (useLocks) {
      const checkChildLocks = (children) => children?.every(child => (child.locked || checkChildLocks(child.children)))
      const updateChildren = (children) => ARRAY.clean(children).map(child => {
        return {
          ...child,
          locked: child.locked || checkChildLocks(child.children),
          children: updateChildren(child.children)
        }
      })

      return data.map(permission => {
        const children = updateChildren(permission.children)
        return {
          ...permission,
          locked: checkChildLocks(children),
          children
        }
      })
    }
    return data
  }, [data, useLocks])

  const customGetSubRows = (row, relativeIndex) => {
    if (ARRAY.hasArray(row.children)) {
      return row.children.map(child => ({ parent: row, ...child }))
    }
    return []
  }

  const expanded = useMemo(() => {

    const reducer = (object, item, index) => {
      const id = `${item.level ? `${item.level}.` : ''}${index}`
      object[id] = searchInput.includes(item.name) || _expanded
      if (ARRAY.hasArray(item.children)) {
        item.children.map(item => ({ ...item, level: id })).reduce(reducer, object)
      }
      return object
    }
    return resolvedData.reduce(reducer, {})
  }, [_expanded, resolvedData, searchInput])

  useEffect(() => {
    for (const [row_id, expandedState] of Object.entries(expanded)) {
      if (tableRef.current?.rowsById?.[row_id]) {
        const row = tableRef.current?.rowsById?.[row_id]
        if (!row.isExpanded) {
          tableRef.current?.toggleRowExpanded(row_id, expandedState)
        }
      }
    }
  }, [expanded, tableRef])

  const globalFilter = useCallback((rows, columnIds, searchQuery) => {
    if (!searchQuery) {
      return rows
    }

    const lowercaseQuery = searchQuery.toLowerCase()
    const rowMatches = (row) => Object.entries(row.values).some(([id, rowValue]) =>
      id === 'name' && typeof rowValue === 'string' && rowValue.toLowerCase().includes(lowercaseQuery)
    ) || row.subRows.some(rowMatches)

    return rows.filter(rowMatches).map(row => ({ ...row, subRows: row.subRows })) // https://github.com/TanStack/table/issues/2064
  }, [])

  const searchCallback = useCallback((searchInput) => {
    setSearchInput(searchInput)
  }, [])

  return (
    <CONTAINER>
      <TableProvider
        tableInstanceRef={tableInstanceRef}
        columns={columns}
        data={resolvedData}
        initialState={OBJECT.hasObject(tableState) ? tableState : { expanded }}
        globalFilter={globalFilter}
        enablePagination={enablePagination}
        enableResizeColumns
        getSubRows={customGetSubRows}
      >
        {/* TODO: move the size dev to above input container */}
        {!!enableSearch && (
          <INPUT_CONTAINER className='cm-p-3 cm-border-solid cm-border-gray-200 dark:cm-border-gray-950 cm-border-0 cm-border-t cm-border-l cm-border-r cm-rounded-t-lg'>
            <ConnectedTableFilter placeholder={PLACEHOLDER_SEARCH_PERMISSION} searchCallback={searchCallback}/>
          </INPUT_CONTAINER>
        )}
        <div className='cm-relative cm-flex-1' style={{ minHeight: '335px' }}>
          <TABLE_CONTAINER>
            <ConnectedTableLayout
              sidecar={<div/>}
              overflow
            >
              <ConnectedTable
                emptyStateProps={{
                  heading: LABEL_NO_PERMISSIONS
                }}
              />
            </ConnectedTableLayout>
          </TABLE_CONTAINER>
        </div>
      </TableProvider>
    </CONTAINER>
  )
}

PermissionsTable.propTypes = {}
export default PermissionsTable
